import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { resolveApiAssetUrl } from '@/lib/api';
import {
  Home,
  Building2,
  LandPlot,
  MapPin,
  Maximize2,
  BedDouble,
  User,
  ChevronLeft,
  ChevronRight,
  Ruler,
  Calendar,
  Phone,
  Mail,
  Bath,
  Zap,
  Percent,
  SquareStack,
} from 'lucide-react';

const typeIcons = {
  haus: Home,
  wohnung: Building2,
  grundstueck: LandPlot,
};

const typeColors = {
  haus: 'bg-[#0f9d94]/15 text-[#0f9d94]',
  wohnung: 'bg-violet-100 text-violet-700',
  grundstueck: 'bg-[#8b5a2b]/15 text-[#8b5a2b]',
};

const typeLabels = {
  haus: 'Haus',
  wohnung: 'Wohnung',
  grundstueck: 'Grundstück',
};

export const PropertyDetailDialog = ({ property, isOpen, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [property?.property_id, isOpen]);

  if (!property) return null;

  const Icon = typeIcons[property.property_type] || MapPin;
  const colorClass = property.is_active === false
    ? 'bg-slate-100 text-slate-700'
    : (typeColors[property.property_type] || typeColors.grundstueck);
  const typeLabel = typeLabels[property.property_type] || property.property_type;
  const hasImages = property.images && property.images.length > 0;
  const userDefinedFields = Object.entries(property.user_defined_fields || {}).filter(([, value]) => Boolean(value));

  const nextImage = () => {
    if (hasImages) {
      setCurrentImageIndex((prev) => (prev + 1) % property.images.length);
    }
  };

  const prevImage = () => {
    if (hasImages) {
      setCurrentImageIndex((prev) => (prev - 1 + property.images.length) % property.images.length);
    }
  };

  const fullAddress = [
    property.street,
    property.street_number,
    property.postcode,
    property.city,
  ].filter(Boolean).join(', ');

  const getImageUrl = (img) => {
    if (!img) return '';
    return resolveApiAssetUrl(img);
  };

  const getEnergyClassColor = (energyClass) => {
    if (!energyClass) return 'bg-gray-100 text-gray-700';
    const cls = energyClass.toUpperCase();
    if (cls === 'A+' || cls === 'A') return 'bg-green-500 text-white';
    if (cls === 'B') return 'bg-lime-500 text-white';
    if (cls === 'C') return 'bg-yellow-400 text-black';
    if (cls === 'D') return 'bg-orange-400 text-white';
    if (cls === 'E') return 'bg-orange-500 text-white';
    if (cls === 'F') return 'bg-red-500 text-white';
    if (cls === 'G' || cls === 'H') return 'bg-red-700 text-white';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="h-[100dvh] max-h-[100dvh] w-screen max-w-none overflow-y-auto rounded-none p-0 sm:h-auto sm:max-h-[90vh] sm:w-auto sm:max-w-4xl sm:rounded-xl"
        data-testid="property-detail-dialog"
      >
        {hasImages ? (
          <div className="relative h-56 bg-stone-100 sm:h-64 md:h-80">
            <img
              src={getImageUrl(property.images[currentImageIndex])}
              alt={`${property.title} - Bild ${currentImageIndex + 1}`}
              className="h-full w-full object-cover"
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1706808849827-7366c098b317?w=800';
              }}
            />
            {property.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-lg transition-colors hover:bg-white sm:left-4 sm:h-10 sm:w-10"
                  data-testid="prev-image-btn"
                  aria-label="Vorheriges Bild"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-lg transition-colors hover:bg-white sm:right-4 sm:h-10 sm:w-10"
                  data-testid="next-image-btn"
                  aria-label="Nächstes Bild"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-sm text-white">
                  {currentImageIndex + 1} / {property.images.length}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="flex h-48 items-center justify-center bg-stone-100">
            <Icon className="h-16 w-16 text-stone-300" />
          </div>
        )}

        <div className="p-4 sm:p-6">
          <DialogHeader className="mb-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <Badge variant="secondary" className={colorClass}>
                    <Icon className="mr-1 h-3 w-3" />
                    {typeLabel}
                    {property.house_type && ` - ${property.house_type}`}
                  </Badge>
                  {property.is_active === false && (
                    <Badge variant="secondary" className="bg-slate-200 text-slate-700">
                      Nicht mehr aktiv
                    </Badge>
                  )}
                </div>

                <DialogTitle className="heading-lg break-words text-left text-slate-900">
                  {property.title}
                </DialogTitle>

                {property.is_active === false && (
                  <p className="mt-2 text-sm text-slate-500">
                    Dieses Objekt wurde aus dem aktiven Bestand entfernt und bleibt nur zur Historie sichtbar.
                  </p>
                )}

                {fullAddress && (
                  <div className="mt-2 flex items-start gap-2 text-stone-500">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                    <span className="break-words">{fullAddress}</span>
                  </div>
                )}

                {property.regionaler_zusatz && (
                  <p className="mt-2 break-words text-sm text-stone-500">{property.regionaler_zusatz}</p>
                )}
              </div>

              <div className="text-left sm:max-w-[220px] sm:text-right">
                {property.price && (
                  <>
                    <p className="text-sm text-stone-500">
                      {property.marketing_type === 'MIETE' ? 'Miete' : 'Kaufpreis'}
                    </p>
                    <div className="break-words text-2xl font-bold text-slate-900">
                      {property.price.toLocaleString('de-DE')} €
                    </div>
                  </>
                )}

                {property.aussen_courtage && (
                  <div className="mt-1 flex items-center gap-1 text-sm text-[#b48d00] sm:justify-end">
                    <Percent className="h-3 w-3 shrink-0" />
                    <span className="break-words">Provision: {property.aussen_courtage}</span>
                  </div>
                )}
              </div>
            </div>
          </DialogHeader>

          <Separator className="my-6" />

          <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 md:grid-cols-4">
            {property.rooms && (
              <div className="rounded-lg bg-stone-50 p-4">
                <div className="mb-1 flex items-center gap-2 text-stone-500">
                  <BedDouble className="h-4 w-4" />
                  <span className="text-sm">Zimmer</span>
                </div>
                <p className="text-xl font-bold text-slate-900">{property.rooms}</p>
              </div>
            )}
            {property.living_area && (
              <div className="rounded-lg bg-stone-50 p-4">
                <div className="mb-1 flex items-center gap-2 text-stone-500">
                  <Maximize2 className="h-4 w-4" />
                  <span className="text-sm">Wohnfläche</span>
                </div>
                <p className="text-xl font-bold text-slate-900">{property.living_area} m²</p>
              </div>
            )}
            {property.nutzflaeche && (
              <div className="rounded-lg bg-stone-50 p-4">
                <div className="mb-1 flex items-center gap-2 text-stone-500">
                  <SquareStack className="h-4 w-4" />
                  <span className="text-sm">Nutzfläche</span>
                </div>
                <p className="text-xl font-bold text-slate-900">{property.nutzflaeche} m²</p>
              </div>
            )}
            {property.property_size && (
              <div className="rounded-lg bg-stone-50 p-4">
                <div className="mb-1 flex items-center gap-2 text-stone-500">
                  <Ruler className="h-4 w-4" />
                  <span className="text-sm">Grundstück</span>
                </div>
                <p className="text-xl font-bold text-slate-900">{property.property_size} m²</p>
              </div>
            )}
            {property.baujahr && (
              <div className="rounded-lg bg-stone-50 p-4">
                <div className="mb-1 flex items-center gap-2 text-stone-500">
                  <Calendar className="h-4 w-4" />
                  <span className="text-sm">Baujahr</span>
                </div>
                <p className="text-xl font-bold text-slate-900">{property.baujahr}</p>
              </div>
            )}
            {property.bedrooms && (
              <div className="rounded-lg bg-stone-50 p-4">
                <div className="mb-1 flex items-center gap-2 text-stone-500">
                  <BedDouble className="h-4 w-4" />
                  <span className="text-sm">Schlafzimmer</span>
                </div>
                <p className="text-xl font-bold text-slate-900">{property.bedrooms}</p>
              </div>
            )}
            {property.bathrooms && (
              <div className="rounded-lg bg-stone-50 p-4">
                <div className="mb-1 flex items-center gap-2 text-stone-500">
                  <Bath className="h-4 w-4" />
                  <span className="text-sm">Badezimmer</span>
                </div>
                <p className="text-xl font-bold text-slate-900">{property.bathrooms}</p>
              </div>
            )}
          </div>

          {(property.energiepass_type || property.energiepass_value || property.energiepass_class) && (
            <div className="mb-6">
              <h4 className="mb-3 flex items-center gap-2 font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                <Zap className="h-5 w-5 text-[#b48d00]" />
                Energieausweis
              </h4>
              <div className="rounded-lg bg-stone-50 p-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
                  {property.energiepass_type && (
                    <div>
                      <span className="text-sm text-stone-500">Ausweistyp</span>
                      <p className="font-medium text-slate-900">
                        {property.energiepass_type === 'VERBRAUCH'
                          ? 'Verbrauchsausweis'
                          : property.energiepass_type === 'BEDARF'
                            ? 'Bedarfsausweis'
                            : property.energiepass_type}
                      </p>
                    </div>
                  )}
                  {property.energiepass_value && (
                    <div>
                      <span className="text-sm text-stone-500">Energiekennwert</span>
                      <p className="font-medium text-slate-900">{property.energiepass_value} kWh/(m²·a)</p>
                    </div>
                  )}
                  {property.energiepass_class && (
                    <div>
                      <span className="text-sm text-stone-500">Energieeffizienzklasse</span>
                      <p className="mt-1">
                        <span className={`inline-block rounded px-3 py-1 font-bold ${getEnergyClassColor(property.energiepass_class)}`}>
                          {property.energiepass_class}
                        </span>
                      </p>
                    </div>
                  )}
                  {property.energiepass_valid_until && (
                    <div>
                      <span className="text-sm text-stone-500">Gültig bis</span>
                      <p className="font-medium text-slate-900">{property.energiepass_valid_until}</p>
                    </div>
                  )}
                  {property.energiepass_heating_type && (
                    <div className="sm:col-span-2">
                      <span className="text-sm text-stone-500">Heizungsart / Energieträger</span>
                      <p className="font-medium text-slate-900 break-words">{property.energiepass_heating_type}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {property.description && (
            <div className="mb-6">
              <h4 className="mb-2 font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Beschreibung</h4>
              <p className="whitespace-pre-line break-words leading-relaxed text-stone-600">{property.description}</p>
            </div>
          )}

          {property.ausstattung && (
            <div className="mb-6">
              <h4 className="mb-2 font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Ausstattung</h4>
              <p className="whitespace-pre-line break-words leading-relaxed text-stone-600">{property.ausstattung}</p>
            </div>
          )}

          {property.lage && (
            <div className="mb-6">
              <h4 className="mb-2 font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Lage</h4>
              <p className="whitespace-pre-line break-words leading-relaxed text-stone-600">{property.lage}</p>
            </div>
          )}

          {property.sonstige_angaben && (
            <div className="mb-6">
              <h4 className="mb-2 font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Sonstige Angaben</h4>
              <p className="whitespace-pre-line break-words leading-relaxed text-stone-600">{property.sonstige_angaben}</p>
            </div>
          )}

          {userDefinedFields.length > 0 && (
            <div className="mb-6">
              <h4 className="mb-3 font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Weitere XML-Felder</h4>
              <div className="space-y-4">
                {userDefinedFields.map(([label, value]) => (
                  <div key={label} className="rounded-lg bg-stone-50 p-4">
                    <p className="mb-2 font-medium text-slate-900 break-words">{label}</p>
                    <p className="whitespace-pre-line break-words leading-relaxed text-stone-600">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {property.responsible_broker && (
            <div className="mb-6">
              <h4 className="mb-2 font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Ansprechpartner</h4>
              <div className="rounded-lg bg-stone-50 p-4">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#b48d00]">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 break-words">{property.responsible_broker}</p>
                    {property.broker_email && (
                      <a href={`mailto:${property.broker_email}`} className="flex items-center gap-1 break-all text-sm text-[#b48d00] hover:underline">
                        <Mail className="h-3 w-3 shrink-0" />
                        {property.broker_email}
                      </a>
                    )}
                  </div>
                </div>
                {property.broker_phone && (
                  <a href={`tel:${property.broker_phone}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#b48d00]">
                    <Phone className="h-4 w-4 shrink-0" />
                    <span className="break-words">{property.broker_phone}</span>
                  </a>
                )}
              </div>
            </div>
          )}

          <div className="mb-6">
            <h4 className="mb-2 font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Standort</h4>
            <div className="rounded-lg bg-stone-50 p-4">
              <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                {property.street && (
                  <div>
                    <span className="text-stone-500">Straße:</span>
                    <span className="ml-2 text-slate-900 break-words">{property.street} {property.street_number}</span>
                  </div>
                )}
                {property.postcode && (
                  <div>
                    <span className="text-stone-500">PLZ:</span>
                    <span className="ml-2 text-slate-900">{property.postcode}</span>
                  </div>
                )}
                {property.city && (
                  <div>
                    <span className="text-stone-500">Ort:</span>
                    <span className="ml-2 text-slate-900 break-words">{property.city}</span>
                  </div>
                )}
                {property.bundesland && (
                  <div>
                    <span className="text-stone-500">Bundesland:</span>
                    <span className="ml-2 text-slate-900 break-words">{property.bundesland}</span>
                  </div>
                )}
                <div>
                  <span className="text-stone-500">Koordinaten:</span>
                  <span className="ml-2 text-slate-900">
                    {Number.isFinite(Number(property.latitude)) && Number.isFinite(Number(property.longitude))
                      ? `${Number(property.latitude).toFixed(5)}, ${Number(property.longitude).toFixed(5)}`
                      : 'Nicht hinterlegt'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full px-6 sm:w-auto"
              data-testid="close-dialog-btn"
            >
              Schließen
            </Button>
          </div>

          {(property.objektnr_extern || property.created_at) && (
            <div className="mt-6 flex flex-col gap-2 border-t border-stone-200 pt-4 text-xs text-stone-400 sm:flex-row sm:items-center sm:gap-4">
              {property.objektnr_extern && <span>Objekt-Nr: {property.objektnr_extern}</span>}
              {property.created_at && (
                <span>Hinzugefügt: {new Date(property.created_at).toLocaleDateString('de-DE')}</span>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyDetailDialog;
